// MARK://dhk8/pos/lib/clients/cashcuts
// Typed client for dhk8-pos-cashcuts — reconciled against the published contract at
// services/dhk8-pos-cashcuts/src/contract.ts.
//
// A cash cut is an IMMUTABLE record (no open/close state machine — the shift itself
// lives in PosShift / dhk8-pos-sessions). Each cut has a `kind`:
//   OPEN      — drawer count at shift start (the "fondo de caja")
//   X_REPORT  — mid-shift snapshot (no drawer reset)
//   CLOSE     — drawer count at shift close
//   Z_REPORT  — end-of-day close (resets the drawer)
// The server computes varianceCents = countedCents - expectedCents. If `denominations`
// are supplied, the server validates countedCents == Σ(denomination*count).
//
// Routes: `${origin}/v1/cashcuts/...`. Envelope: { ok, data } | bare T.

import { apiFetch, getTenantId, svcUrl } from '../api.js'
import { gqlClient } from '../gql.js'
import { PosCashCutsQuery, PosCashCutQuery, PosCreateCashCutMutation, PaymentTotalsByMethodQuery, PosCashCutExpectedQuery } from '../queries/cashcuts.queries.js'

// dhk8-pos-cashcuts expone /v1/cashcuts. Ingress route:
// /v1/dhk8-pos-cashcuts/v1/cashcuts → el servicio ve /v1/cashcuts.
const BASE = svcUrl('dhk8-pos-cashcuts', '/v1/cashcuts')

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }
async function fetchData<T>(url: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  const body = await apiFetch<Envelope<T> | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<T>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

// ── DTOs (mirror of services/dhk8-pos-cashcuts/src/contract.ts) ─────────────────

// Lane #162 — CASH_DROP/CASH_PICKUP son movimientos intra-turno (no cierran shift)
export type CashCutKind = 'OPEN' | 'CLOSE' | 'X_REPORT' | 'Z_REPORT' | 'CASH_DROP' | 'CASH_PICKUP'

/** A bill/coin denomination, value in cents (e.g. 50000 = $500 bill), and how many. */
export interface Denomination {
  denomination: number
  count: number
}

export interface CashCutDto {
  id: string
  tenantId: string
  branchId: string
  shiftId: string
  cashierId: string
  kind: CashCutKind | string
  expectedCents: number
  countedCents: number
  /** countedCents - expectedCents (server-computed). Negative = faltante, positive = sobrante. */
  varianceCents: number
  denominations: Denomination[] | null
  notes: string | null
  createdAt: string
}

export interface CreateCashCutInput {
  tenantId: string
  branchId: string
  shiftId: string
  cashierId: string
  kind: CashCutKind
  expectedCents?: number
  countedCents?: number
  denominations?: Denomination[]
  notes?: string
  /** Tipifica el movimiento (PETTY_CASH_PURCHASE | PETTY_CASH_CHANGE | ...). */
  reason?: string
  /** Correlador: UUID de la PettyCashPurchase asociada. */
  purchaseRef?: string
}

export interface ListCashCutsQuery {
  tenantId: string
  branchId?: string
  shiftId?: string
  kind?: CashCutKind
  page?: number
  limit?: number
}

export interface CashCutListResponse {
  items: CashCutDto[]
  total: number
  page: number
  limit: number
}

// ── helpers ─────────────────────────────────────────────────────────────────────

/** Σ(denomination*count) — what the drawer adds up to, in cents. */
export function sumDenominations(d: Denomination[]): number {
  return d.reduce((acc, x) => acc + x.denomination * x.count, 0)
}

// ── operations ──────────────────────────────────────────────────────────────────

export async function listCashCuts(query: ListCashCutsQuery, signal?: AbortSignal): Promise<CashCutListResponse> {
  // F4.2 / ADR-0009: lectura por el grafo (pos-cashcuts, tenant del JWT). Escrituras REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const vars: { branchId?: string; shiftId?: string; kind?: CashCutKind; page?: number; limit?: number } = {}
  if (query.branchId) vars.branchId = query.branchId
  if (query.shiftId) vars.shiftId = query.shiftId
  if (query.kind) vars.kind = query.kind
  if (query.page) vars.page = query.page
  if (query.limit) vars.limit = query.limit
  const res = await gqlClient.query(PosCashCutsQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  const pageRes = res.data?.cashCuts
  return {
    items: (pageRes?.items ?? []) as unknown as CashCutDto[],
    total: pageRes?.total ?? 0,
    page: pageRes?.page ?? (query.page ?? 1),
    limit: pageRes?.limit ?? (query.limit ?? 20),
  }
}

export async function getCashCut(id: string, signal?: AbortSignal): Promise<CashCutDto> {
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosCashCutQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const c = res.data?.cashCut
  if (!c) throw new Error(`NOT_FOUND: cashcut ${id}`)
  return c as unknown as CashCutDto
}

export async function createCashCut(input: CreateCashCutInput): Promise<CashCutDto> {
  // F8.1 / ADR-0009: ESCRITURA por el grafo (createCashCut mutation, tenant del JWT).
  const res = await gqlClient.mutation(PosCreateCashCutMutation, { input: { branchId: input.branchId, shiftId: input.shiftId, cashierId: input.cashierId, ...(input.kind ? { kind: input.kind } : {}), ...(input.expectedCents !== undefined ? { expectedCents: input.expectedCents } : {}), ...(input.countedCents !== undefined ? { countedCents: input.countedCents } : {}), ...(input.denominations ? { denominations: input.denominations } : {}), ...(input.notes ? { notes: input.notes } : {}), ...(input.reason ? { reason: input.reason } : {}), ...(input.purchaseRef ? { purchaseRef: input.purchaseRef } : {}) } }).toPromise()
  if (res.error) throw res.error
  const c = res.data?.createCashCut
  if (!c) throw new Error('CREATE_CASHCUT_FAILED')
  return c as unknown as CashCutDto
}

/** Efectivo esperado del turno, derivado por el SERVIDOR (money-path autoritativo). */
export interface CashCutExpected {
  /** fondo + ventas efectivo + ingresos − retiros */
  expectedCents: number
  fondoCents: number
  cashSalesCents: number
  pickupsCents: number
  dropsCents: number
}

/**
 * Pide al backend el efectivo esperado del turno (desglosado). El servidor lo
 * deriva de los cortes (fondo/ingresos/retiros) + ventas en efectivo del turno —
 * fuente única de verdad. El cliente ya NO calcula el esperado (eso causaba el
 * "remanente" por usar ventas del día y no restar retiros).
 */
export async function getCashCutExpected(shiftId: string, branchId?: string, signal?: AbortSignal): Promise<CashCutExpected> {
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosCashCutExpectedQuery, { shiftId, ...(branchId ? { branchId } : {}) }, ctx).toPromise()
  if (res.error) throw res.error
  const e = res.data?.cashCutExpected
  if (!e) throw new Error('No se pudo calcular el efectivo esperado')
  return { expectedCents: e.expectedCents, fondoCents: e.fondoCents, cashSalesCents: e.cashSalesCents, pickupsCents: e.pickupsCents, dropsCents: e.dropsCents }
}

/** Most recent cash cut for this tenant (optionally branch/shift/kind), or null. */
export async function latestCashCut(
  opts: { branchId?: string; shiftId?: string; kind?: CashCutKind } = {},
  signal?: AbortSignal,
): Promise<CashCutDto | null> {
  const tenantId = getTenantId() ?? ''
  const res = await listCashCuts(
    { tenantId, ...(opts.branchId ? { branchId: opts.branchId } : {}), ...(opts.shiftId ? { shiftId: opts.shiftId } : {}), ...(opts.kind ? { kind: opts.kind } : {}), limit: 1 },
    signal,
  ).catch(() => ({ items: [], total: 0, page: 1, limit: 1 } as CashCutListResponse))
  return res.items[0] ?? null
}

// ════════════════════════════════════════════════════════════════════════════════
// CASH-DRAWER / SHIFT MANAGEMENT (the /caja screen)
//
// Higher-level façade over the cash-cuts service that models an *operational shift*:
// apertura de turno (fondo) → movimientos (entradas/salidas) → arqueo & cierre, plus
// corte X (lectura) / corte Z (cierre) summaries. Until dhk8-pos-sessions is wired in,
// real builds derive the shift from the OPEN/CLOSE cash-cuts (createCashCut above) and
// keep movements client-side. Under PREVIEW_STUB everything reads/writes `stubShift`.
// ════════════════════════════════════════════════════════════════════════════════

export type CashMoveKind = 'IN' | 'OUT'

export interface CashMove {
  id: string
  kind: CashMoveKind
  /** Always positive, in cents. */
  amountCents: number
  reason: string
  at: string
}

/** Per-payment-method roll-up for the X/Z report card. */
export interface PaymentMethodTotal {
  method: string
  label: string
  ticketCount: number
  totalCents: number
}

export interface OpenShift {
  id: string
  status: 'OPEN'
  openedAt: string
  cashierName: string
  /** Declared opening float, in cents (Σ openingDenoms). */
  openingFloatCents: number
  openingDenoms: Denomination[]
  moves: CashMove[]
}

/** A finished arqueo / cierre — the reconciliation result for the X or Z report. */
export interface ShiftReport {
  shiftId: string
  kind: 'X' | 'Z'
  generatedAt: string
  openedAt: string
  cashierName: string
  openingFloatCents: number
  /** Sales by payment method (CASH first). */
  byMethod: PaymentMethodTotal[]
  ticketCount: number
  grossSalesCents: number
  cashSalesCents: number
  cashInCents: number
  cashOutCents: number
  /** openingFloat + cashSales + cashIn − cashOut */
  expectedCashCents: number
  /** Σ closing denominations (null on an X read with no count). */
  countedCashCents: number | null
  /** countedCash − expectedCash (positive = sobrante, negative = faltante). */
  varianceCents: number | null
}

export interface CloseShiftInput {
  closingDenoms: Denomination[]
  notes?: string
}

function nowIso(): string { return new Date().toISOString() }
function rid(prefix: string): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  return c?.randomUUID ? `${prefix}_${c.randomUUID().slice(0, 8)}` : `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

// ── PREVIEW_STUB in-memory shift state ──────────────────────────────────────────
// Lazily seeded from SEED_SHIFT (imported lazily to avoid a hard module cycle with
// seed-preview.ts which itself never imports this file's stub state).

let stubShift: OpenShift | null = null
let stubLastReport: ShiftReport | null = null
let stubSeeded = false

async function ensureStubSeed(): Promise<void> {
  if (stubSeeded) return
  stubSeeded = true
  const m = await import('../seed-preview.js')
  if (m.SEED_SHIFT) stubShift = structuredClone(m.SEED_SHIFT)
}

/** Demo payment-method roll-up — SOLO modo preview (PREVIEW_STUB). El build real
 *  jamás lo usa: lee los pagos reales con realByMethod() (D3). */
function stubByMethod(): PaymentMethodTotal[] {
  return [
    { method: 'CASH', label: 'Efectivo', ticketCount: 14, totalCents: 312_000 },
    { method: 'CARD_CREDIT', label: 'Tarjeta crédito', ticketCount: 9, totalCents: 268_500 },
    { method: 'CARD_DEBIT', label: 'Tarjeta débito', ticketCount: 6, totalCents: 141_900 },
    { method: 'TRANSFER', label: 'Transferencia', ticketCount: 2, totalCents: 58_000 },
  ]
}

// PosPayment.method ∈ { CASH | CARD | TRANSFER | VOUCHER | OTHER } (db-pos schema).
const METHOD_LABELS: Record<string, string> = {
  CASH: 'Efectivo', CARD: 'Tarjeta', CARD_CREDIT: 'Tarjeta crédito',
  CARD_DEBIT: 'Tarjeta débito', TRANSFER: 'Transferencia', VOUCHER: 'Vale', OTHER: 'Otro',
}
function methodLabel(method: string): string {
  return METHOD_LABELS[method] ?? method
}

/** D3 — roll-up REAL de pagos por método del tenant en la ventana del turno
 *  [openedAt, now). Lee pos-orders.paymentTotalsByMethod por el grafo (tenant del
 *  JWT, RLS). CASH primero para el card del corte. Si no hay pagos, devuelve []. */
async function realByMethod(shift: OpenShift, signal?: AbortSignal): Promise<PaymentMethodTotal[]> {
  const from = shift.openedAt
  const to = nowIso()
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PaymentTotalsByMethodQuery, { from, to }, ctx).toPromise()
  if (res.error) throw new Error(res.error.message)
  const rows = res.data?.paymentTotalsByMethod ?? []
  const mapped: PaymentMethodTotal[] = rows.map((r) => ({
    method: r.method,
    label: methodLabel(r.method),
    ticketCount: r.ticketCount,
    totalCents: r.totalCents,
  }))
  // CASH primero, luego por monto desc (estable para el card del corte).
  return mapped.sort((a, b) =>
    (a.method === 'CASH' ? -1 : 0) - (b.method === 'CASH' ? -1 : 0) || b.totalCents - a.totalCents,
  )
}

function buildReport(shift: OpenShift, kind: 'X' | 'Z', closingDenoms: Denomination[] | null, byMethod: PaymentMethodTotal[]): ShiftReport {
  const cashSalesCents = byMethod.find((b) => b.method === 'CASH')?.totalCents ?? 0
  const grossSalesCents = byMethod.reduce((a, b) => a + b.totalCents, 0)
  const ticketCount = byMethod.reduce((a, b) => a + b.ticketCount, 0)
  const cashInCents = shift.moves.filter((m) => m.kind === 'IN').reduce((a, m) => a + m.amountCents, 0)
  const cashOutCents = shift.moves.filter((m) => m.kind === 'OUT').reduce((a, m) => a + m.amountCents, 0)
  const expectedCashCents = shift.openingFloatCents + cashSalesCents + cashInCents - cashOutCents
  const countedCashCents = closingDenoms ? sumDenominations(closingDenoms) : null
  return {
    shiftId: shift.id, kind, generatedAt: nowIso(), openedAt: shift.openedAt, cashierName: shift.cashierName,
    openingFloatCents: shift.openingFloatCents, byMethod, ticketCount, grossSalesCents, cashSalesCents,
    cashInCents, cashOutCents, expectedCashCents, countedCashCents,
    varianceCents: countedCashCents === null ? null : countedCashCents - expectedCashCents,
  }
}

// ── operations ──────────────────────────────────────────────────────────────────

/** The currently open shift for this drawer, or null if the drawer is closed. */
export async function getOpenShift(signal?: AbortSignal): Promise<OpenShift | null> {
  const { PREVIEW_STUB } = await import('../seed-preview.js')
  if (PREVIEW_STUB) { await ensureStubSeed(); return stubShift ? structuredClone(stubShift) : null }
  // Real build: a shift is "open" when the latest cut is an OPEN (no CLOSE/Z after).
  const open = await latestCashCut({ kind: 'OPEN' }, signal)
  const close = await latestCashCut({ kind: 'CLOSE' }, signal)
  const z = await latestCashCut({ kind: 'Z_REPORT' }, signal)
  if (!open) return null
  const closedAfter = [close, z].some((c) => c && c.createdAt > open.createdAt)
  if (closedAfter) return null
  return {
    id: open.shiftId, status: 'OPEN', openedAt: open.createdAt, cashierName: open.cashierId,
    openingFloatCents: open.countedCents, openingDenoms: open.denominations ?? [], moves: [],
  }
}

/** Open a shift with the counted opening float. */
export async function openShift(input: { openingDenoms: Denomination[]; cashierName?: string; notes?: string }): Promise<OpenShift> {
  const { PREVIEW_STUB } = await import('../seed-preview.js')
  const openingFloatCents = sumDenominations(input.openingDenoms.filter((d) => d.count > 0))
  if (PREVIEW_STUB) {
    await ensureStubSeed()
    stubShift = {
      id: rid('shift'), status: 'OPEN', openedAt: nowIso(), cashierName: input.cashierName ?? 'Caja 1',
      openingFloatCents, openingDenoms: input.openingDenoms.filter((d) => d.count > 0), moves: [],
    }
    stubLastReport = null
    return structuredClone(stubShift)
  }
  const tenantId = getTenantId() ?? ''
  const shiftId = rid('shift')
  await createCashCut({
    tenantId, branchId: '', shiftId, cashierId: input.cashierName ?? '', kind: 'OPEN',
    expectedCents: 0, countedCents: openingFloatCents,
    denominations: input.openingDenoms.filter((d) => d.count > 0),
    ...(input.notes ? { notes: input.notes } : {}),
  })
  return { id: shiftId, status: 'OPEN', openedAt: nowIso(), cashierName: input.cashierName ?? 'Caja 1', openingFloatCents, openingDenoms: input.openingDenoms.filter((d) => d.count > 0), moves: [] }
}

/** Log a cash-in / cash-out movement during the open shift. */
export async function recordCashMove(input: { kind: CashMoveKind; amountCents: number; reason: string }): Promise<CashMove> {
  const { PREVIEW_STUB } = await import('../seed-preview.js')
  const move: CashMove = { id: rid('mv'), kind: input.kind, amountCents: Math.abs(Math.round(input.amountCents)), reason: input.reason.trim(), at: nowIso() }
  if (PREVIEW_STUB) {
    await ensureStubSeed()
    if (!stubShift) throw new Error('No hay un turno abierto')
    stubShift.moves = [move, ...stubShift.moves]
    return structuredClone(move)
  }
  // Real build: no movements endpoint yet — keep it client-side (caller persists).
  return move
}

/** Corte X — a mid-shift reading (does not reset the drawer). */
export async function getXReport(opts: { closingDenoms?: Denomination[] } = {}, signal?: AbortSignal): Promise<ShiftReport> {
  const shift = await getOpenShift(signal)
  if (!shift) throw new Error('No hay un turno abierto')
  const { PREVIEW_STUB } = await import('../seed-preview.js')
  const byMethod = PREVIEW_STUB ? stubByMethod() : await realByMethod(shift, signal)
  const report = buildReport(shift, 'X', opts.closingDenoms ?? null, byMethod)
  if (PREVIEW_STUB) {
    const tenantId = getTenantId() ?? '__preview__'
    await createCashCut({ tenantId, branchId: '', shiftId: shift.id, cashierId: shift.cashierName, kind: 'X_REPORT', expectedCents: report.expectedCashCents, countedCents: report.countedCashCents ?? 0 }).catch(() => undefined)
  }
  return report
}

/** Corte Z — close the shift: count closing cash, reconcile, reset the drawer. */
export async function closeShift(input: CloseShiftInput): Promise<ShiftReport> {
  const { PREVIEW_STUB } = await import('../seed-preview.js')
  if (PREVIEW_STUB) {
    await ensureStubSeed()
    if (!stubShift) throw new Error('No hay un turno abierto')
    const report = buildReport(stubShift, 'Z', input.closingDenoms.filter((d) => d.count > 0), stubByMethod())
    stubLastReport = report
    stubShift = null
    return structuredClone(report)
  }
  const shift = await getOpenShift()
  if (!shift) throw new Error('No hay un turno abierto')
  const byMethod = await realByMethod(shift)
  const report = buildReport(shift, 'Z', input.closingDenoms.filter((d) => d.count > 0), byMethod)
  const tenantId = getTenantId() ?? ''
  await createCashCut({
    tenantId, branchId: '', shiftId: shift.id, cashierId: shift.cashierName, kind: 'Z_REPORT',
    expectedCents: report.expectedCashCents, countedCents: report.countedCashCents ?? 0,
    denominations: input.closingDenoms.filter((d) => d.count > 0),
    ...(input.notes ? { notes: input.notes } : {}),
  })
  return report
}

/** Corte Z — the report from the most recently closed shift (preview convenience). */
export async function getZReport(): Promise<ShiftReport | null> {
  const { PREVIEW_STUB } = await import('../seed-preview.js')
  if (PREVIEW_STUB) return stubLastReport ? structuredClone(stubLastReport) : null
  return null
}
